import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PanelField } from '@/UI/PanelField/PanelField';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { AddItemButton } from '@/components/Common/AddItemButton/AddItemButton';
import { I18N_KEYS } from '@/language/keys';
import { Input } from '@/UI/Input/Input';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { useIsHovered } from '@/hooks/useIsHovered';
import { LIST_MAX_CHARS } from '@/components/Nodes/consts/validations.consts';
import { useListSelectorView, useNodeStringArrayValue } from '../FlowContext';
import type { NodeId } from 'web';
import type { ListSelectorData } from '@/types/node';

const getRandomId = () => crypto.randomUUID();

export function ListSelectorFields({ id, data }: { id: NodeId; data: ListSelectorData }) {
  const { t } = useTranslation();
  const view = useListSelectorView(id);
  const options = useNodeStringArrayValue(id);
  const hasInput = view.hasInput();
  const disabled = hasInput;

  // const hasStringInput = view.hasStringInput();
  const { selected /* delimeter */ } = data;
  // const setDelimiter = useCallback(
  //   (value: string) => {
  //     view.setDelimiter(value);
  //   },
  //   [view],
  // );

  const handleSelection = useCallback((newIndex: number) => view.setSelected(newIndex), [view]);

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [pendingFocus, setPendingFocus] = useState<{ index: number; expectedLength: number } | null>(null);
  const missingRowIdsRef = useRef<Record<number, string>>({});
  const [rowIds, setRowIds] = useState<string[]>(() => options.map(() => getRandomId()));

  const getRowId = useCallback(
    (index: number) => {
      return rowIds[index] ?? (missingRowIdsRef.current[index] ??= getRandomId());
    },
    [rowIds],
  );

  useEffect(() => {
    // Reconcile rowIds only when the backing list length changes (i.e. options updated asynchronously).
    if (rowIds.length !== options.length) {
      setRowIds(Array.from({ length: options.length }, () => getRandomId()));
      inputRefs.current = [];
      missingRowIdsRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  useLayoutEffect(() => {
    if (pendingFocus == null || options.length !== pendingFocus.expectedLength) {
      return;
    }

    const el = inputRefs.current[pendingFocus.index];
    if (el) {
      el.focus();
      el.select();
      setPendingFocus(null);
    }
  }, [options.length, pendingFocus]);

  const handleValueChange = useCallback(
    (value: string, index: number) => {
      const newOptions = options.map((option, i) => (i === index ? value : option));
      view.setOptions(newOptions);
    },
    [view, options],
  );

  const handleAddItem = useCallback(
    (afterIndex?: number) => {
      const insertIndex = afterIndex == null ? options.length : afterIndex + 1;
      const newOptions = [...options.slice(0, insertIndex), '', ...options.slice(insertIndex)];
      setRowIds((prev) => {
        const next = [...prev];
        next.splice(insertIndex, 0, getRandomId());
        return next;
      });

      let nextSelected = selected;
      if (insertIndex <= selected) {
        nextSelected = selected + 1;
      }

      setPendingFocus({ index: insertIndex, expectedLength: options.length + 1 });
      view.setOptions(newOptions);
      if (nextSelected !== selected) view.setSelected(nextSelected);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getRandomId, options, selected, view],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newOptions = options.filter((_, i) => i !== index);
      setRowIds((prev) => prev.filter((_, i) => i !== index));
      view.setOptions(newOptions);
    },
    [view, options],
  );

  const handleDeleteAndFocusPrev = useCallback(
    (index: number) => {
      if (options.length <= 1) {
        return;
      }

      const focusIndex = Math.max(0, index - 1);

      let nextSelected = selected;
      if (index < selected) {
        nextSelected = selected - 1;
      } else if (index === selected) {
        nextSelected = Math.max(0, selected - 1);
      }

      setPendingFocus({ index: focusIndex, expectedLength: options.length - 1 });
      void handleDelete(index);
      if (nextSelected !== selected) {
        view.setSelected(nextSelected);
      }
    },
    [handleDelete, options.length, selected, view],
  );

  const handleTab = useCallback(
    (index: number, direction: 'next' | 'prev') => {
      if (options.length === 0) {
        return;
      }

      const jump = direction === 'next' ? 1 : -1;
      const nextIndex = (index + jump + options.length) % options.length;
      setPendingFocus({ index: nextIndex, expectedLength: options.length });
    },
    [options.length],
  );

  const handleEnter = useCallback(
    (index: number) => {
      if (options[index]?.length === 0) {
        return;
      }

      handleAddItem(index);
    },
    [handleAddItem, options],
  );

  return (
    <Box key={`${id}-dynamic-field`} sx={{ position: 'relative', mb: 2 }}>
      <PanelField
        label={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.OPTIONS_LABEL)}
        tooltipText={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.OPTIONS_TOOLTIP)}
        showSlotOnHoverOnly
        disabled={false /* TODO: hasInput? */}
      >
        <FlexCol sx={{ width: '100%', gap: 0.75, cursor: 'default' }}>
          {/* 
            hasStringInput && (
          <FlexCenVer sx={{ gap: 1 }}>
            <Typography variant="body-sm-rg">{t(I18N_KEYS.RECIPE_MAIN.NODES.ARRAY.SPLIT_TEXT_BY)}</Typography>
            <TextInput value={delimiter} onSubmit={setDelimiter} sx={{ width: '40px' }} />
          </FlexCenVer>) 
          */}
          {options.map((option, index) => (
            <ListItem
              key={getRowId(index)}
              item={option}
              selected={selected === index}
              index={index}
              onValueChange={handleValueChange}
              onDelete={handleDelete}
              onDeleteAndFocusPrev={handleDeleteAndFocusPrev}
              onEnter={handleEnter}
              onTab={handleTab}
              inputRef={(el) => {
                inputRefs.current[index] = el;
              }}
              editable={!disabled}
              disableDelete={options.length === 1}
              onSelect={handleSelection}
            />
          ))}
          {!disabled && (
            <div>
              <AddItemButton
                show
                onClick={() => handleAddItem()}
                text={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ADD_ITEM)}
              />
            </div>
          )}
        </FlexCol>
      </PanelField>
    </Box>
  );
}

function ListItem({
  editable = false,
  item,
  index,
  onValueChange,
  onDelete,
  onDeleteAndFocusPrev,
  onEnter,
  onTab,
  inputRef,
  disableDelete = false,
  selected,
  onSelect,
}: {
  editable?: boolean;
  item: string;
  index: number;
  onValueChange: (value: string, index: number) => void;
  onDelete: (index: number) => void;
  onDeleteAndFocusPrev: (index: number) => void;
  onEnter: (index: number) => void;
  onTab: (index: number, direction: 'next' | 'prev') => void;
  inputRef: (el: HTMLInputElement | null) => void;
  disableDelete?: boolean;
  selected: boolean;
  onSelect: (index: number) => void;
}) {
  const { t } = useTranslation();
  const { isHovered, ...hoverProps } = useIsHovered();

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      onValueChange(evt.target.value, index);
    },
    [index, onValueChange],
  );
  const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);
  const handleSelect = useCallback(() => onSelect?.(index), [index, onSelect]);

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (!editable) {
        return;
      }

      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        (evt.currentTarget as HTMLInputElement).blur();
        return;
      }

      if (evt.key === 'Tab') {
        evt.preventDefault();
        evt.stopPropagation();
        onTab(index, evt.shiftKey ? 'prev' : 'next');
        return;
      }

      if (evt.key === 'Enter') {
        evt.preventDefault();
        evt.stopPropagation();
        if (item.length === 0) {
          return;
        }
        return onEnter(index);
      }

      if (evt.key === 'Backspace' && item.length === 0 && !disableDelete) {
        evt.preventDefault();
        evt.stopPropagation();
        onDeleteAndFocusPrev(index);
      }
    },
    [disableDelete, editable, index, item, onDeleteAndFocusPrev, onEnter, onTab],
  );

  return (
    <FlexCenVer sx={{ width: '100%', height: 32 }} {...hoverProps}>
      <Radio checked={selected} onClick={handleSelect} size="small" />
      <Input
        fullWidth
        disabled={!editable}
        value={item}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        noBlurOnEnter
        inputProps={{ maxLength: LIST_MAX_CHARS }}
        inputRef={inputRef}
        placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.PLACEHOLDER, { index: index + 1 })}
      />
      {editable && isHovered ? (
        <AppXBtn disabled={disableDelete} onClick={handleDelete} size={20} sx={{ ml: 0.5 }} />
      ) : null}
    </FlexCenVer>
  );
}
